import React, { useMemo } from 'react';
import styled from 'styled-components';
import { MdDashboard, MdSchedule, MdChat, MdPerson, MdAssignment, MdEventAvailable, MdOutlineQuiz, MdEventNote, MdClose, MdSupportAgent, MdStar, MdMenuBook, MdQrCodeScanner } from 'react-icons/md';
import AuthService from '../services/authService';
import theme from '../styles/theme';

const MOBILE_QUERY = '(max-width: 768px)';

const Overlay = styled.div`
  display: none;

  @media ${MOBILE_QUERY} {
    display: ${(p) => (p.$open ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    z-index: 45;
    background: rgba(15, 23, 42, 0.5);
    animation: fadeIn 0.2s ease;

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  }
`;

const Aside = styled.aside`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  transition: width 0.25s ease;
  width: ${(p) => (p.$open ? '256px' : '64px')};
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);

  @media ${MOBILE_QUERY} {
    width: 280px;
    max-width: 82vw;
    border-right: none;
    transition: transform 0.28s ease;
    transform: translateX(${(p) => (p.$open ? '0' : '-100%')});
    box-shadow: ${(p) => (p.$open ? '0 0 32px rgba(15, 23, 42, 0.28)' : 'none')};
  }
`;

const TopBar = styled.div`
  height: 68px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 16px;

  @media ${MOBILE_QUERY} {
    justify-content: space-between;
  }
`;

const BrandBadge = styled.img`
  height: 34px;
  width: 34px;
  flex-shrink: 0;
  border-radius: 9px;
  display: block;
  box-shadow: 0 4px 10px rgba(67, 56, 202, 0.25);
`;

const BrandLabel = styled.span`
  font-weight: 700;
  font-size: 15px;
  color: #0f172a;
  letter-spacing: -0.01em;
`;

const CloseButton = styled.button`
  display: none;

  @media ${MOBILE_QUERY} {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    font-size: 20px;

    &:hover { background: ${theme.primaryLight}; color: ${theme.primaryDark}; }
  }
`;

const Nav = styled.nav`
  flex: 1;
  padding: 16px 12px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const NavLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 10px;
  padding: 10px 12px;
  color: ${(p) => (p.$active ? '#ffffff' : '#475569')};
  background: ${(p) => (p.$active ? theme.gradient : 'transparent')};
  box-shadow: ${(p) => (p.$active ? '0 4px 10px rgba(67, 56, 202, 0.28)' : 'none')};
  text-decoration: none;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  svg { font-size: 18px; flex-shrink: 0; }

  &:hover {
    background: ${(p) => (p.$active ? theme.gradient : theme.primaryLight)};
    color: ${(p) => (p.$active ? '#ffffff' : theme.primaryDark)};
  }

  @media ${MOBILE_QUERY} {
    padding: 13px 12px;
    font-size: 15px;
  }
`;

const Footer = styled.div`
  flex-shrink: 0;
  border-top: 1px solid #e5e7eb;
  padding: 16px;
  color: #6b7280;
  font-size: 12px;
`;

const RoleBadge = styled.div`
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fef3c7;
  border: 1px solid #facc15;
  color: #92400e;
  font-size: 11px;
  font-weight: 600;
`;

export default function StudentSidebar({ isOpen, onToggle }) {
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const userInfo = AuthService.getUserInfo();
  const isParent = userInfo?.type === 'parent';
  const hasRegistration = Boolean(userInfo?.registrationNumber);

  const navigation = useMemo(() => {
    const items = [
      { name: 'Dashboard', href: '/app/dashboard', icon: <MdDashboard /> },
      { name: 'Submissions', href: '/app/submissions', icon: <MdAssignment /> },
      { name: 'Attendance', href: '/app/attendance', icon: <MdEventAvailable /> },
      { name: 'Event Attendance', href: '/app/event-attendance', icon: <MdEventNote /> },
    ];
    // GPS-scan attendance is a physical-presence action tied to the
    // student's own device -- parents shouldn't scan on a student's behalf,
    // and the backend already rejects the scan itself for non-student keys.
    if (!isParent) {
      items.push({ name: 'Scan Attendance', href: '/app/scan-attendance', icon: <MdQrCodeScanner /> });
    }
    items.push(
      { name: 'Timetable', href: '/app/timetable', icon: <MdSchedule /> },
      { name: 'My Exam', href: '/app/my-exam', icon: <MdOutlineQuiz /> },
      { name: 'Shared Courses', href: '/app/shared-courses', icon: <MdMenuBook /> },
      { name: 'Ticket', href: '/app/chat', icon: <MdChat /> },
      { name: 'My Favorite Lecturer', href: '/app/favorite-lecturer', icon: <MdStar /> },
    );
    if (hasRegistration) {
      items.push({ name: 'Contact HOD', href: '/app/contact', icon: <MdSupportAgent /> });
    }
    items.push({ name: 'Profile', href: '/app/profile', icon: <MdPerson /> });
    return items;
  }, [hasRegistration, isParent]);

  const isActive = (href) => {
    if (href === '/app/dashboard') return currentPath === href;
    return currentPath.startsWith(href) && href !== '#';
  };

  // On mobile the drawer is only reachable while open, so closing on nav
  // tap/backdrop click is always the intended action there; desktop never
  // renders the overlay/close button so this handler is a no-op for it.
  const closeOnMobile = () => {
    const isMobile = typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches;
    if (isMobile && isOpen && onToggle) onToggle();
  };

  return (
    <>
      <Overlay $open={isOpen} onClick={closeOnMobile} />
      <Aside $open={isOpen}>
        <TopBar>
          {isOpen ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <BrandBadge src={`${process.env.PUBLIC_URL}/logo192.png`} alt="MYC" />
              <BrandLabel>{isParent ? 'Parent' : 'Student'}</BrandLabel>
            </div>
          ) : (
            <BrandBadge src={`${process.env.PUBLIC_URL}/logo192.png`} alt="MYC" />
          )}
          <CloseButton onClick={onToggle} aria-label="Close menu">
            <MdClose />
          </CloseButton>
        </TopBar>
        <Nav>
          {navigation.map((item) => {
            const itemActive = isActive(item.href);
            return (
              <div key={item.name} style={{ marginBottom: 4 }}>
                <NavLink
                  href={item.href}
                  $active={itemActive}
                  title={!isOpen ? item.name : undefined}
                  onClick={closeOnMobile}
                >
                  <span style={{ display: 'flex' }}>{item.icon}</span>
                  {isOpen && <span>{item.name}</span>}
                </NavLink>
              </div>
            );
          })}
        </Nav>
        {isOpen && (
          <Footer>
            <div>
              <div style={{ fontWeight: 600, color: '#111827' }}>{isParent ? 'Parent Panel' : 'Student Panel'}</div>
              {isParent && (
                <RoleBadge>
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: '#f59e0b' }} />
                  <span>Parent view</span>
                </RoleBadge>
              )}
              <div style={{ marginTop: 8 }}>Version 1.0.0</div>
            </div>
          </Footer>
        )}
      </Aside>
    </>
  );
}
